import { Icon } from '@platform/components';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import cx from 'classix';
import { useState } from 'react';

export function StatusTable<T>({
  data,
  columns,
}: {
  data: T[];
  columns: ColumnDef<T>[];
}) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className='mt-4 overflow-hidden rounded-xl border border-gray-100 bg-white'>
      {data.length === 0 ? (
        <div className='px-4 py-8 text-center text-sm text-gray-400'>No applicants in this group</div>
      ) : (
        <table className='w-full border-collapse text-left text-sm'>
            <thead className='sticky top-0 z-10 bg-gray-50'>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className='h-[75px]'>
                  {headerGroup.headers.map((header, i) => {
                    const canSort = header.column.getCanSort();
                    const sortDir = header.column.getIsSorted() as 'asc' | 'desc' | false;
                    return (
                      <th
                        key={header.id}
                        className={cx(
                          'align-middle text-md font-semibold text-gray-500',
                          i === 0 ? 'pl-4' : 'border-l border-gray-100 px-4',
                        )}
                      >
                        <div
                          className={cx(
                            'flex items-center gap-1',
                            canSort && 'cursor-pointer select-none',
                          )}
                          onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {sortDir === 'asc' ?
                            <Icon type='chevron-up' size='12' />
                          : sortDir === 'desc' ?
                            <Icon type='chevron-down' size='12' />
                          : canSort ?
                            <span
                              className='inline-flex flex-col items-center justify-center'
                              aria-hidden='true'
                            >
                              <Icon type='chevron-up' size='12' />
                              <Icon type='chevron-down' size='12' additionalClassNames='-mt-1' />
                            </span>
                          : null}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className='border-t border-gray-100'>
                  {row.getVisibleCells().map((cell, i) => (
                    <td
                      key={cell.id}
                      className={cx(
                        'py-3 align-middle',
                        i === 0 ? 'pl-4' : 'border-l border-gray-100 px-4',
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
      )}
    </div>
  );
}
